import React, { useState, useEffect } from 'react';
import { useI18n } from '../../i18n/I18nContext';
import api from '../../utils/api';

function OwnerSettings() {
  var i18n = useI18n();
  var t = i18n.t;

  var loadingState = useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];

  var savingState = useState(false);
  var saving = savingState[0];
  var setSaving = savingState[1];

  var settingsState = useState({
    native_language: 'en',
    target_language: 'th',
    language_display_mode: 'dual',
    default_house_rules: '',
    default_checkin_info: '',
    default_checkout_info: '',
    default_emergency_info: '',
    default_phone: '',
    default_whatsapp: '',
    default_line_id: '',
    default_website: '',
    default_email: ''
  });
  var settings = settingsState[0];
  var setSettings = settingsState[1];

  var languagesState = useState([]);
  var languages = languagesState[0];
  var setLanguages = languagesState[1];

  var messageState = useState(null);
  var message = messageState[0];
  var setMessage = messageState[1];

  useEffect(function() {
    loadSettings();
  }, []);

  function loadSettings() {
    api.get('/owner/settings')
      .then(function(data) {
        setSettings(data.settings);
        setLanguages(data.supported_languages || []);
        setLoading(false);
      })
      .catch(function(err) {
        console.error('Failed to load settings:', err);
        setLoading(false);
      });
  }

  function handleChange(field, value) {
    setSettings(function(prev) {
      var updated = Object.assign({}, prev);
      updated[field] = value;
      return updated;
    });
  }

  function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    setMessage(null);

    api.put('/owner/settings', settings)
      .then(function() {
        setMessage({ type: 'success', text: 'Settings saved successfully' });
        setSaving(false);
      })
      .catch(function(err) {
        setMessage({ type: 'error', text: err.message || 'Failed to save settings' });
        setSaving(false);
      });
  }

  function handleApplyToAll() {
    if (!window.confirm('Apply your default templates to all your properties? This will overwrite any custom values that are set to use defaults.')) {
      return;
    }

    setSaving(true);
    api.post('/owner/settings/apply-to-all', {})
      .then(function(data) {
        setMessage({ type: 'success', text: 'Defaults applied to ' + data.properties_updated + ' properties' });
        setSaving(false);
      })
      .catch(function(err) {
        setMessage({ type: 'error', text: err.message || 'Failed to apply defaults' });
        setSaving(false);
      });
  }

  if (loading) {
    return React.createElement('div', { className: 'flex justify-center', style: { padding: '40px' } },
      React.createElement('div', { className: 'spinner' })
    );
  }

  return React.createElement('div', null,
    React.createElement('h1', { style: { fontSize: '24px', fontWeight: '600', marginBottom: '24px' } }, 
      t('owner.settings') || 'Settings'
    ),

    message && React.createElement('div', {
      className: 'card',
      style: {
        marginBottom: '16px',
        padding: '12px 16px',
        background: message.type === 'success' ? '#d4edda' : '#f8d7da',
        color: message.type === 'success' ? '#155724' : '#721c24',
        borderRadius: 'var(--radius)'
      }
    }, message.text),

    React.createElement('form', { onSubmit: handleSave },
      // Language Preferences Section
      React.createElement('div', { className: 'card', style: { marginBottom: '16px' } },
        React.createElement('h2', { style: { fontSize: '18px', fontWeight: '600', marginBottom: '16px' } }, 
          'Language Preferences'
        ),

        React.createElement('div', { className: 'grid', style: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' } },
          React.createElement('div', null,
            React.createElement('label', { className: 'form-label', style: { display: 'block', marginBottom: '8px', fontWeight: '500' } }, 'Native Language'),
            React.createElement('select', {
              className: 'form-control',
              value: settings.native_language || 'en',
              onChange: function(e) { handleChange('native_language', e.target.value); }
            },
              languages.map(function(lang) {
                return React.createElement('option', { key: lang.code, value: lang.code }, 
                  lang.name + ' (' + lang.native_name + ')'
                );
              })
            )
          ),
          React.createElement('div', null,
            React.createElement('label', { className: 'form-label', style: { display: 'block', marginBottom: '8px', fontWeight: '500' } }, 'Translation Language'),
            React.createElement('select', {
              className: 'form-control',
              value: settings.target_language || 'th',
              onChange: function(e) { handleChange('target_language', e.target.value); }
            },
              languages.map(function(lang) {
                return React.createElement('option', { key: lang.code, value: lang.code }, 
                  lang.name + ' (' + lang.native_name + ')'
                );
              })
            )
          ),
          React.createElement('div', null,
            React.createElement('label', { className: 'form-label', style: { display: 'block', marginBottom: '8px', fontWeight: '500' } }, 'Display Mode'),
            React.createElement('select', {
              className: 'form-control',
              value: settings.language_display_mode || 'dual',
              onChange: function(e) { handleChange('language_display_mode', e.target.value); }
            },
              React.createElement('option', { value: 'dual' }, 'Show both languages'),
              React.createElement('option', { value: 'native_only' }, 'Native only'),
              React.createElement('option', { value: 'target_only' }, 'Translation only')
            )
          )
        )
      ),

      // Default Property Templates Section
      React.createElement('div', { className: 'card', style: { marginBottom: '16px' } },
        React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' } },
          React.createElement('h2', { style: { fontSize: '18px', fontWeight: '600' } }, 
            'Default Property Templates'
          ),
          React.createElement('button', {
            type: 'button',
            className: 'btn btn-secondary',
            onClick: handleApplyToAll,
            disabled: saving
          }, 'Apply to All Properties')
        ),
        React.createElement('p', { className: 'text-secondary', style: { marginBottom: '16px', fontSize: '14px' } },
          'These templates will be used as defaults for new properties. You can customize each property individually.'
        ),

        React.createElement('div', { style: { marginBottom: '16px' } },
          React.createElement('label', { className: 'form-label', style: { display: 'block', marginBottom: '8px', fontWeight: '500' } }, 'Default House Rules'),
          React.createElement('textarea', { style: { width: '100%' },
            className: 'form-control',
            rows: 6,
            value: settings.default_house_rules || '',
            onChange: function(e) { handleChange('default_house_rules', e.target.value); },
            placeholder: 'e.g., No smoking indoors. Quiet hours 10pm-8am. Maximum 4 guests.'
          })
        ),

        React.createElement('div', { style: { marginBottom: '16px' } },
          React.createElement('label', { className: 'form-label', style: { display: 'block', marginBottom: '8px', fontWeight: '500' } }, 'Default Check-in Info'),
          React.createElement('textarea', { style: { width: '100%' },
            className: 'form-control',
            rows: 5,
            value: settings.default_checkin_info || '',
            onChange: function(e) { handleChange('default_checkin_info', e.target.value); },
            placeholder: 'e.g., Check-in: 2:00 PM. Key located in lockbox by front door.'
          })
        ),

        React.createElement('div', { style: { marginBottom: '16px' } },
          React.createElement('label', { className: 'form-label', style: { display: 'block', marginBottom: '8px', fontWeight: '500' } }, 'Default Check-out Info'),
          React.createElement('textarea', { style: { width: '100%' },
            className: 'form-control',
            rows: 5,
            value: settings.default_checkout_info || '',
            onChange: function(e) { handleChange('default_checkout_info', e.target.value); },
            placeholder: 'e.g., Check-out: 11:00 AM. Please leave keys on counter.'
          })
        ),

        React.createElement('div', null,
          React.createElement('label', { className: 'form-label', style: { display: 'block', marginBottom: '8px', fontWeight: '500' } }, 'Default Emergency Info'),
          React.createElement('textarea', { style: { width: '100%' },
            className: 'form-control',
            rows: 5,
            value: settings.default_emergency_info || '',
            onChange: function(e) { handleChange('default_emergency_info', e.target.value); },
            placeholder: 'e.g., Emergency: 191. Nearest hospital: Bangkok Hospital Pattaya.'
          })
        )
      ),

      // Default Contact Info Section
      React.createElement('div', { className: 'card', style: { marginBottom: '16px' } },
        React.createElement('h2', { style: { fontSize: '18px', fontWeight: '600', marginBottom: '16px' } }, 
          'Default Contact Info'
        ),
        React.createElement('p', { className: 'text-secondary', style: { marginBottom: '16px', fontSize: '14px' } },
          'Contact details shown to guests by default.'
        ),

        React.createElement('div', { className: 'grid', style: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' } },
          React.createElement('div', null,
            React.createElement('label', { className: 'form-label', style: { display: 'block', marginBottom: '8px', fontWeight: '500' } }, 'Phone'),
            React.createElement('input', {
              type: 'text',
              className: 'form-control',
              value: settings.default_phone || '',
              onChange: function(e) { handleChange('default_phone', e.target.value); },
              placeholder: '+66 XX XXX XXXX'
            })
          ),
          React.createElement('div', null,
            React.createElement('label', { className: 'form-label', style: { display: 'block', marginBottom: '8px', fontWeight: '500' } }, 'WhatsApp'),
            React.createElement('input', {
              type: 'text',
              className: 'form-control',
              value: settings.default_whatsapp || '',
              onChange: function(e) { handleChange('default_whatsapp', e.target.value); },
              placeholder: '+66 XX XXX XXXX'
            })
          ),
          React.createElement('div', null,
            React.createElement('label', { className: 'form-label', style: { display: 'block', marginBottom: '8px', fontWeight: '500' } }, 'Line ID'),
            React.createElement('input', {
              type: 'text',
              className: 'form-control',
              value: settings.default_line_id || '',
              onChange: function(e) { handleChange('default_line_id', e.target.value); },
              placeholder: '@lineid'
            })
          ),
          React.createElement('div', null,
            React.createElement('label', { className: 'form-label', style: { display: 'block', marginBottom: '8px', fontWeight: '500' } }, 'Website'),
            React.createElement('input', {
              type: 'text',
              className: 'form-control',
              value: settings.default_website || '',
              onChange: function(e) { handleChange('default_website', e.target.value); },
              placeholder: 'https://example.com'
            })
          ),
          React.createElement('div', null,
            React.createElement('label', { className: 'form-label', style: { display: 'block', marginBottom: '8px', fontWeight: '500' } }, 'Email'),
            React.createElement('input', {
              type: 'email',
              className: 'form-control',
              value: settings.default_email || '',
              onChange: function(e) { handleChange('default_email', e.target.value); },
              placeholder: 'contact@example.com'
            })
          )
        )
      ),

      // Save Button
      React.createElement('div', { style: { display: 'flex', justifyContent: 'flex-end' } },
        React.createElement('button', {
          type: 'submit',
          className: 'btn btn-primary',
          disabled: saving
        }, saving ? 'Saving...' : 'Save Settings')
      )
    )
  );
}

export default OwnerSettings;
